import { UIColor, $colors, $color } from '../ui/color';
import { $el } from '../ui/elements';
import { UISVGContent, $svg, UISVG } from '../ui/svg';
import { UIRect, UIPoint, UIRelPos } from '../ui/types';
import { UIViewAddSpec } from '../ui/view';
import { CHXYPlot, CHXYAxes } from './axes';
import { CHLegendItem } from './legend';
import { CHPoint } from './point';
import { CHScale } from './scale';

export interface CHLinePlotOptions {
    color?: UIColor;
    label?: string;
}

export class CHLinePlot<XValue, YValue> implements UISVGContent, CHXYPlot<XValue, YValue> {
    root: SVGGElement;
    color: UIColor;
    label?: string;
    axes: CHXYAxes<XValue, YValue> | undefined;

    private _data: CHPoint<XValue, YValue>[];
    private _rect?: UIRect;
    private _x_scale!: CHScale<XValue>;
    private _y_scale!: CHScale<YValue>;
    private _circle!: SVGCircleElement;
    private _enabled: boolean;

    constructor(data: CHPoint<XValue, YValue>[], options: CHLinePlotOptions = {}) {
        this.root = $svg.G('ch-line');

        this._enabled = true;
        this._data = data;

        this.color = options.color || $colors.cyan;
        this.label = options.label;
    }

    minMax(): [[XValue, XValue], [YValue, YValue]] {
        const data = this._data;

        let min_y: YValue = Infinity as any;
        let max_y: YValue = -Infinity as any;

        for(const p of data) {
            let y = p.y;
            if(y < min_y) min_y = y;
            if(y > max_y) max_y = y;
        }

        if(!data.length)
            return [[Infinity as any, -Infinity as any], [min_y, max_y]]

        return [[data[0].x, data[data.length - 1].x], [min_y, max_y]];
    }

    layout(rect: UIRect, x_scale: CHScale<XValue>, y_scale: CHScale<YValue>) {
        const root = this.root;
        root.innerHTML = '';
        const data = this._data;
        this._rect = rect;
        this._x_scale = x_scale;
        this._y_scale = y_scale;

        if(data.length < 2) return;

        this.root.setAttribute('transform', `translate(${rect[0]},${rect[1]})`);

        const w = rect[2] - rect[0];
        const h = rect[3] - rect[1];

        let x = x_scale.normalize(data[0].x) * w;
        let y = (1 - y_scale.normalize(data[0].y)) * h;

        let d = `M${x},${y}`;

        for(let i = 1; i < data.length; i++) {
            x = x_scale.normalize(data[i].x) * w;
            y = (1 - y_scale.normalize(data[i].y)) * h;
            d += `L${x},${y}`;
        }

        const path = $svg.PATH(undefined, d);
        path.style.stroke = $color.HTML(this.color);
        root.appendChild(path);

        const circle = this._circle = $svg.CIRCLE(0, 0, 4);
        circle.style.display = 'none';
        root.appendChild(circle);
    }

    hitTest(point: UIPoint | undefined) {
        const circle = this._circle;

        if(point) {
            const w = this._rect![2] - this._rect![0];
            const h = this._rect![3] - this._rect![1];
            const x = this._x_scale.denormalize(point[0] / w);

            let i: number;
            const values = this._data;
            for(i = 0; i < values.length - 1; i++) {
                if(x < values[i + 1].x) {
                    if((values[i + 1].x as any) - (x as any) < (x as any) - (values[i].x as any))
                        i++;
                    break;
                }
            }

            const value = values[i];
            const x_norm = this._x_scale.normalize(value.x);
            const y_norm = this._y_scale.normalize(value.y);

            const p_x = x_norm * w;
            const p_y = (1 - y_norm) * h;
            const dist = (p_x - point[0]) * (p_x - point[0]) + (p_y - point[1]) * (p_y - point[1]);

            circle.style.display = '';
            circle.setAttribute('cx', (p_x) as any);
            circle.setAttribute('cy', (p_y) as any);

            const tooltip: UIViewAddSpec = [this._x_scale.tipFormat(value.x) + '\u00a0\u00a0\u00a0\u00a0' + this._y_scale.tipFormat(value.y)]
            if(this.label)
                tooltip.unshift(this.label, $el.BR());

            return {
                distance: dist,
                text: tooltip,
                pos: [p_x + this._rect![0], p_y + this._rect![1] - 12] as UIPoint,
                rel_pos: UIRelPos.Above
            }
        } else {
            circle.style.display = 'none';

            return;
        }
    }

    render(svg: UISVG) {
        svg.add(this.root);
    }

    fillLegend(items: CHLegendItem[]) {
        if(this.label)
            items.push(this as CHLegendItem);
    }

    get enabled() {
        return this._enabled;
    }

    set enabled(e: boolean) {
        this._enabled = e;
        this.root.classList.toggle('u-gone', !e);
        this.axes && this.axes.onPlotEnabled(this);
    }
}
